
import { defineComponent, reactive } from 'vue'
import { useRoute } from 'vue-router'

// module components
import OktellCalculator from '@/modules/OktellNewOrderModule/components/OktellCalculator/index.vue'
import OktellCalculatorResults from '@/modules/OktellNewOrderModule/components/OktellCalculatorResults/index.vue'
import OktellCreateLtlOrder from '@/modules/OktellNewOrderModule/components/OktellCreateLtlOrder/index.vue'

// types
import type { CalculatorComponentPayload, CalculatorPhysicalWeights, CalculatorVolumeWeights, CalculatorAdditionalService, BillingCalculateCostResponseAdditionalServices, BillingCalculateCostParams } from '@/core/types/billing.types'
import type { CallcenterOrderPayload, OrderPayloadI } from '@/core/types/orders.types'

// realisations
import { billing } from '@/core/realisations/billing.realisation'
import { orders } from '@/core/realisations/orders.realisation'

// composable
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        OktellCalculator,
        OktellCalculatorResults,
        OktellCreateLtlOrder,
    },
    setup() {
        const route = useRoute()
        const notifications = useNotifications()

        const operatorEmail = route.query.email as string | undefined

        const calculator_state = reactive({
            is_loading: false,
            is_form_submited: false,
            is_loaded: false,
            //
            company_id: null as number | null,
            tariff_id: null as number | null,
            //
            from_city_id: null as number | null,
            from_city_name: null as string | null,
            where_city_id: null as number | null,
            where_city_name: null as string | null,
            is_cities_equals: false,
            //
            is_has_pickup: false,
            is_has_self_delivery: false,
            //
            physicalWeights: [] as CalculatorPhysicalWeights,
            volumeWeights: [] as CalculatorVolumeWeights,
            //
            declaredPrice: null as number | null,
            productName: null as string | null,
            //
            additionalServicesDelivery: [] as CalculatorAdditionalService[],
            //
            econom: {
                additionalServiceDelivery:
                    null as BillingCalculateCostResponseAdditionalServices | null,
                additionalServiceTake:
                    null as BillingCalculateCostResponseAdditionalServices | null,
                result: 0,
                insuranceCost: 0,
                packagingCost: 0,
                unloadCost: 0,
                days: 0,
                usedWeightInKg: 0,
                weightTypeToCalculate: null as string | null,
            },
            express: {
                additionalServiceDelivery:
                    null as BillingCalculateCostResponseAdditionalServices | null,
                additionalServiceTake:
                    null as BillingCalculateCostResponseAdditionalServices | null,
                result: 0,
                insuranceCost: 0,
                packagingCost: 0,
                unloadCost: 0,
                days: 0,
                usedWeightInKg: 0,
                weightTypeToCalculate: null as string | null,
            },
        })

        const order_state = reactive({
            is_opened: false,
            is_created: false,
            is_loading: false,
        })

        const resetState = () => {
            calculator_state.is_form_submited = false
            calculator_state.is_loaded = false

            calculator_state.tariff_id = null
            calculator_state.company_id = null

            calculator_state.from_city_name = null
            calculator_state.from_city_id = null
            calculator_state.where_city_name = null
            calculator_state.where_city_id = null
            calculator_state.is_cities_equals = false

            calculator_state.is_has_pickup = false
            calculator_state.is_has_self_delivery = false

            calculator_state.physicalWeights = []
            calculator_state.volumeWeights = []

            calculator_state.declaredPrice = null
            calculator_state.productName = null
            calculator_state.additionalServicesDelivery = []

            calculator_state.econom.additionalServiceDelivery = null
            calculator_state.econom.additionalServiceTake = null
            calculator_state.econom.result = 0
            calculator_state.econom.insuranceCost = 0
            calculator_state.econom.packagingCost = 0
            calculator_state.econom.unloadCost = 0
            calculator_state.econom.days = 0
            calculator_state.econom.usedWeightInKg = 0
            calculator_state.econom.weightTypeToCalculate = null

            calculator_state.express.additionalServiceDelivery = null
            calculator_state.express.additionalServiceTake = null
            calculator_state.express.result = 0
            calculator_state.express.insuranceCost = 0
            calculator_state.express.packagingCost = 0
            calculator_state.express.unloadCost = 0
            calculator_state.express.days = 0
            calculator_state.express.usedWeightInKg = 0
            calculator_state.express.weightTypeToCalculate = null
        }

        const onCalculatorSubmit = async (payload: CalculatorComponentPayload) => {
            resetState()
            calculator_state.is_form_submited = true

            calculator_state.tariff_id = payload.tariff_id
            calculator_state.company_id = payload.company_id

            calculator_state.from_city_name = payload.from_city.name
            calculator_state.from_city_id = payload.from_city.id
            calculator_state.where_city_name = payload.where_city.name
            calculator_state.where_city_id = payload.where_city.id

            calculator_state.is_cities_equals = payload.from_city.id === payload.where_city.id

            calculator_state.volumeWeights = [...payload.volumeWeights]
            calculator_state.physicalWeights = [...payload.physicalWeights]

            calculator_state.is_has_pickup = payload.is_has_pickup
            calculator_state.is_has_self_delivery = payload.is_has_self_delviery

            if (payload.declaredPrice) {
                calculator_state.declaredPrice = payload.declaredPrice
            }

            if (payload.productName) {
                calculator_state.productName = payload.productName
            }

            calculator_state.additionalServicesDelivery = payload.additionalServicesDelivery

            try {
                calculator_state.is_loading = true

                const econom_params: BillingCalculateCostParams = {
                    isFromMainCity: payload.direction.isFromMainCity,
                    isNearbyCities: payload.direction.isNearbyCities,
                    zoneId: payload.direction.zoneId,
                    shipmentTypeId: 1,
                    hasPickup: Number(payload.is_has_pickup),
                    hasSelfDelivery: Number(payload.is_has_self_delviery),
                    isOrderPrice: Number(payload.isOrderPrice),
                    volumeWeights: calculator_state.volumeWeights,
                    physicalWeights: calculator_state.physicalWeights,
                    additionalServicesDelivery: calculator_state.additionalServicesDelivery,
                    fromCityId: calculator_state.from_city_id as number,
                    whereCityId: calculator_state.where_city_id as number,
                }

                const express_params: BillingCalculateCostParams = {
                    isFromMainCity: payload.direction.isFromMainCity,
                    isNearbyCities: payload.direction.isNearbyCities,
                    zoneId: payload.direction.zoneId,
                    shipmentTypeId: 2,
                    hasPickup: Number(payload.is_has_pickup),
                    hasSelfDelivery: Number(payload.is_has_self_delviery),
                    isOrderPrice: Number(payload.isOrderPrice),
                    volumeWeights: calculator_state.volumeWeights,
                    physicalWeights: calculator_state.physicalWeights,
                    additionalServicesDelivery: calculator_state.additionalServicesDelivery,
                    fromCityId: calculator_state.from_city_id as number,
                    whereCityId: calculator_state.where_city_id as number,
                }

                if (payload.company_id) {
                    econom_params.companyId = payload.company_id
                    express_params.companyId = payload.company_id
                }

                if (payload.declaredPrice) {
                    econom_params.declaredPrice = payload.declaredPrice
                    express_params.declaredPrice = payload.declaredPrice
                }

                if (payload.productName) {
                    econom_params.productName = payload.productName
                    express_params.productName = payload.productName
                }

                const econom_response = await billing.calculateCost(econom_params)
                calculator_state.econom.additionalServiceDelivery =
                    econom_response.result.additionalServiceDelivery
                calculator_state.econom.additionalServiceTake = econom_response.result.additionalServiceTake
                calculator_state.econom.result = econom_response.result.result
                calculator_state.econom.insuranceCost = econom_response.result.insuranceCost
                calculator_state.econom.packagingCost = econom_response.result.packagingCost
                calculator_state.econom.unloadCost = econom_response.result.unloadCost
                calculator_state.econom.usedWeightInKg = econom_response.result.usedWeightInKg
                calculator_state.econom.weightTypeToCalculate = econom_response.result.weightTypeToCalculate
                calculator_state.econom.days = payload.direction.standart_delivery_time

                if (!calculator_state.is_cities_equals) {
                    const express_response = await billing.calculateCost(express_params)
                    calculator_state.express.additionalServiceDelivery =
                        express_response.result.additionalServiceDelivery
                    calculator_state.express.additionalServiceTake =
                        express_response.result.additionalServiceTake
                    calculator_state.express.result = express_response.result.result
                    calculator_state.express.insuranceCost = express_response.result.insuranceCost
                    calculator_state.express.packagingCost = express_response.result.packagingCost
                    calculator_state.express.unloadCost = express_response.result.unloadCost
                    calculator_state.express.usedWeightInKg = express_response.result.usedWeightInKg
                    calculator_state.express.weightTypeToCalculate =
                        express_response.result.weightTypeToCalculate
                    calculator_state.express.days = payload.direction.express_delivery_time
                }

                calculator_state.is_loaded = true
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При рассчете стоимости произошла ошибка',
                })
            } finally {
                calculator_state.is_loading = false
            }
        }

        const onOrderSubmit = async (payload: OrderPayloadI) => {
            try {
                order_state.is_loading = true

                await orders.createCallcenterOrder({ ...payload, callcenter_email: operatorEmail as string })

                order_state.is_created = true
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При создании заказа произошла ошибка' })
            } finally {
                order_state.is_loading = false
            }
        }

        const onOrderReset = () => {
            order_state.is_created = false
        }

        return {
            calculator_state,
            order_state,
            onCalculatorSubmit,
            onOrderSubmit,
            onOrderReset,
        }
    }
})
